import React, { useCallback, useEffect, useState } from "react";
import { ROUTES } from "~/constants";
import { fetchProductExchange, fetchRefundCalculate, fetchSimilarProducts } from "~/services";
import styles from "./return.module.scss";
import ProductItem from "~/components/productItem/ProductItem";
import classnames from "classnames";
import {
    findSelectedProductValues,
    getProductsData,
    getRemainingWithdrawalDays,
    getSelectedLanguageCode,
    getSelectedRefundOptionObject,
    isComplaintsDisabledForAllItems,
    isGlobalIframeModalActive,
    isRefundOptionSelected,
    isWithdrawalActive,
    isWithdrawalBlocked,
    isWithdrawalSelectionLocked,
    removeSelectedProductValues,
    updateSelectedProductValues,
} from "~/cores/appCore";
import { getOrderStatusUrl } from "~/utils";
import getServerSideProps from "~/app/getServerSideProps";
import { isResaleActive, isShipmentMethodPageEnabled } from "~/cores/shipment.core";
import ProductsFlexWrapper, { ProductFlexItemWrapper } from "~/components/productItem/ProductsFlexWrapper";
import { TurnrAlert, TurnrFontAwesomeIcon, TurnrWarningAlert } from "~/components/alerts/TurnrAlert";
import { faClock } from "@fortawesome/pro-regular-svg-icons";
import { ExternalLink } from "~/components/links/ExternalLink";
import { humanReadableDateTime, useServiceHook } from "rylak-common";
import { Button } from "~/components/buttons/Buttons";
import ProductModal from "./methodSelector/ProductModal";
import RefundOptions from "../confirmation/RefundOptions";
import RefundCalculation from "~/components/refundCalculation/RefundCalculation";
import { isProductExchangeComplaintDisabled, isProductExchangeDisabled, isProductMethodSelectorEnabled } from "~/cores/productCore";
import { isReturnSelected } from "~/cores/selectedProductValuesCore";
import { IFRAME_ACTIONS } from "rylak-common/src/constants";

function ProductRow(props) {
    const { appProps, product, brandName, setSelectedProductsValues, setShowModal, sessionId, onProductExchangeUpdated, onSimilarProductsUpdated, system, foreignOrderId, email, getLocaleText } =
        props;
    const selectedProductValues = findSelectedProductValues(appProps, product.sessionItemId);

    const { serviceData: productExchangeServiceData } = useServiceHook({
        appProps,
        fetchFn: fetchProductExchange,
        fetchRequested: !!product.sessionItemId && (!isProductExchangeDisabled(product, { appProps }) || !isProductExchangeComplaintDisabled(product, { appProps })),
        fetchData: {
            system,
            sessionId: sessionId,
            productId: product.sessionItemId,
        },
    });

    const { serviceData: similarProductServiceData } = useServiceHook({
        appProps,
        fetchFn: fetchSimilarProducts,
        fetchRequested: !!product.sessionItemId,
        fetchData: {
            sessionId: sessionId,
            productId: product.actualProductId,
            purchasePrice: product?.purchasePrice,
        },
    });

    useEffect(() => {
        onProductExchangeUpdated({
            productExchangeServiceData,
            productId: product.sessionItemId,
        });
    }, [productExchangeServiceData]);

    useEffect(() => {
        onSimilarProductsUpdated({
            similarProductServiceData,
            productId: product.sessionItemId,
        });
    }, [similarProductServiceData]);

    const returnId = product?.returnId;
    const methodSelectorEnabled = isProductMethodSelectorEnabled(product, { appProps });

    const externalOrderStatusUrl = getOrderStatusUrl({
        brandName,
        foreignOrderId: foreignOrderId,
        email: email,
        returnId: returnId,
        language: getSelectedLanguageCode(appProps),
    });
    const withdrawalActive = isWithdrawalActive(appProps);
    // Deselection is only blocked in the "whole order, locked" mode. The other withdrawal modes
    // (individually selectable / pre-select all) still let the customer remove items.
    const selectionLocked = isWithdrawalSelectionLocked(appProps);
    return (
        <div style={{ position: "relative" }}>
            <ProductItem
                topRightText={withdrawalActive && isReturnSelected(selectedProductValues) ? getLocaleText("WITHDRAWAL") : undefined}
                onClose={
                    !selectionLocked &&
                    selectedProductValues &&
                    (() => {
                        setSelectedProductsValues(removeSelectedProductValues(appProps, product.sessionItemId));
                    })
                }
                dataTestId={"product-item-" + product.sessionItemId}
                contentOpacity={!methodSelectorEnabled}
                onClick={
                    methodSelectorEnabled
                        ? () => {
                              if (returnId) {
                                  window.open(externalOrderStatusUrl);
                              } else {
                                  setShowModal(product.sessionItemId);
                              }
                          }
                        : undefined
                }
                {...props}
                product={product}
                selectedProductValues={selectedProductValues}
                subTitle={
                    !!returnId && (
                        <div>
                            <ExternalLink
                                showIcon
                                className={styles["already-returned-wrapper"]}
                                href={externalOrderStatusUrl}
                                text={
                                    <div>
                                        {getLocaleText("view-return-status")} {humanReadableDateTime({ dateTime: product?.returnDate, shortVersion: false })}
                                    </div>
                                }
                            />
                        </div>
                    )
                }
                className={classnames(!methodSelectorEnabled && styles["product-item-already-returned"])}
            ></ProductItem>
        </div>
    );
}

const ReturnPage = function (props) {
    let {
        appProps,
        navigate,
        loginServiceData,
        selectedProductsValues,
        setSelectedProductsValues,
        productsExchangeServiceData,
        similarProductsServiceData,
        sessionId,
        returnTimeExceeded,
        addAlert,
        getLocaleText,
        withdrawal,
    } = props;

    const [showModal, setShowModal] = useState(false);
    const [latestModalProductId, setLatestModalProductId] = useState(null);

    const messageParentToOpenModalIframe = useCallback(
        (productId) => {
            window.parent.postMessage(
                {
                    action: IFRAME_ACTIONS.TO_MODAL_IFRAME_OPEN_IFRAME_PRODUCT_MODAL,
                    relayToOtherIframe: true,
                    data: {
                        appPropsAsString: JSON.stringify(appProps),
                        productId,
                    },
                },
                "*",
            );
        },
        [appProps],
    );

    const listenerFunction = useCallback(
        (event) => {
            if (event?.data?.action === IFRAME_ACTIONS.TO_MAIN_IFRAME_SAVE_PRODUCT_CHOICE) {
                const data = event?.data?.data;
                const refundOptionType = event?.data?.refundOptionType;
                setSelectedProductsValues(updateSelectedProductValues(appProps, latestModalProductId, data));
                if (refundOptionType) {
                    appProps?.setSelectedRefundOption(refundOptionType);
                }
            }
        },
        [latestModalProductId],
    );

    useEffect(() => {
        window.addEventListener("message", listenerFunction);
        return () => {
            window.removeEventListener("message", listenerFunction);
        };
    }, [listenerFunction]);

    const onShowModal = useCallback(
        (productId) => {
            if (isGlobalIframeModalActive(appProps)) {
                setLatestModalProductId(productId);
                messageParentToOpenModalIframe(productId);
            } else {
                setLatestModalProductId(productId);
                setShowModal(true);
            }
        },
        [messageParentToOpenModalIframe, appProps],
    );

    const withdrawalBlocked = isWithdrawalBlocked(appProps);

    const handleContinueClick = async () => {
        if (withdrawalBlocked) {
            // The withdrawal time limit has passed - the backend will reject the submit, so stop here.
            addAlert({
                content: getLocaleText("withdrawal-limit-passed"),
            });
        } else if (selectedProductsValues.length === 0) {
            addAlert({
                content: getLocaleText("return-items.next-back.error_text"),
            });
        } else {
            navigate(
                {
                    pathname: isShipmentMethodPageEnabled(appProps) ? ROUTES.SHIPMENT_METHOD_PAGE.pathname : ROUTES.CONFIRMATION_PAGE.pathname,
                },
                undefined,
                { shallow: true },
            );
        }
    };

    let products = getProductsData(appProps);

    const { serviceData: refundCalculateServiceData, fetchServiceData: fetchRefundCalculateFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchRefundCalculate,
        fetchData: {
            sessionId,
            loginServiceData,
        },
    });

    useEffect(() => {
        if (selectedProductsValues?.length > 0) {
            fetchRefundCalculateFn({ selectedProductsValues });
        }
    }, [selectedProductsValues]);

    const selectedRefundOptionObject = getSelectedRefundOptionObject(appProps);

    const canMakeComplaints = !isComplaintsDisabledForAllItems(appProps);
    return (
        <div data-test-id="return-page" data-test-email={appProps?.email} data-test-order-id={appProps?.orderId}>
            {loginServiceData.data && (
                <div style={{ marginTop: "20px" }}>
                    {withdrawal === true && !withdrawalBlocked && (
                        <TurnrWarningAlert
                            fullWidth
                            style={{ marginBottom: "20px" }}
                            content={
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <TurnrFontAwesomeIcon icon={faClock} width={"18px"} />
                                    <span>{getLocaleText("withdrawal-banner.body", { days: getRemainingWithdrawalDays(appProps) })}</span>
                                </div>
                            }
                        />
                    )}
                    {withdrawalBlocked && <TurnrAlert style={{ marginBottom: "20px" }}>{getLocaleText("withdrawal-limit-passed")}</TurnrAlert>}
                    {returnTimeExceeded && withdrawal !== true && (
                        <TurnrAlert style={{ marginBottom: "20px" }}>
                            {getLocaleText("return-limit-passed")}
                            {canMakeComplaints && getLocaleText("return-limit-passed-complaint-part")}
                        </TurnrAlert>
                    )}
                    <ProductsFlexWrapper
                        productsComponents={products.map((product) => {
                            return (
                                <ProductFlexItemWrapper key={product.productVariantId}>
                                    <ProductRow {...props} setShowModal={onShowModal} product={product} />
                                </ProductFlexItemWrapper>
                            );
                        })}
                    >
                        {isRefundOptionSelected(appProps) && <RefundOptions {...props} />}
                        <ProductFlexItemWrapper newRow={true}>
                            <div style={{ marginBottom: "15px" }}>
                                <RefundCalculation
                                    {...props}
                                    totalPriceHeader={getLocaleText(isResaleActive(props) ? "receipt-estimated-resale" : "receipt-estimated-refund")}
                                    totalPriceSubHeader={getLocaleText("receipt-can-change")}
                                    collapsible={true}
                                    refundOption={selectedRefundOptionObject}
                                    refundCalculateServiceData={refundCalculateServiceData}
                                />
                            </div>
                        </ProductFlexItemWrapper>
                    </ProductsFlexWrapper>
                    <Button
                        appProps={appProps}
                        fullWidth={true}
                        dataTestId={"continue-btn"}
                        text={getLocaleText("continue")}
                        style={{ marginTop: "10px" }}
                        disabled={withdrawalBlocked}
                        onClick={handleContinueClick}
                    />
                    {showModal && (
                        <ProductModal
                            {...props}
                            appProps={appProps}
                            showModal={showModal}
                            product={products.find((p) => p.sessionItemId === latestModalProductId)}
                            onModalClose={() => setShowModal(false)}
                            productExchangeServiceData={productsExchangeServiceData[latestModalProductId]}
                            similarProductServiceData={similarProductsServiceData[latestModalProductId]}
                            selectedProductValues={findSelectedProductValues(appProps, latestModalProductId)}
                            onSave={(data, refundOptionType) => {
                                setSelectedProductsValues(updateSelectedProductValues(appProps, latestModalProductId, data));
                                if (refundOptionType) {
                                    appProps?.setSelectedRefundOption(refundOptionType);
                                }
                                setShowModal(false);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export { getServerSideProps };
export default ReturnPage;
